import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { HeadlessPageWrapper } from 'templates/PageWrapper/PageWrapper';
import { useBaoSelector } from 'utils/hooks/redux';
import { ReportData, Report, IReportParams } from '../types';
import { months } from 'utils/constants/common';
import { StyledCellText } from 'molecules/Table/styles/Text.styles';
import Table from 'molecules/Table';
import {
  getCustomerInvoiceColor,
  getCustomerInvoiceInfo,
} from 'organisms/CloudOpsReportAdmin/common';
import PaginationActions from 'molecules/Table/TablePagination/Actions';
import { StyledTableContainer } from 'molecules/Table/styles';
import { StyledTablePagination } from 'molecules/Table/styles/Pagination.styles';
import { StyledPagePaper } from 'organisms/CloudOpsReportAdmin/CloudOpsReportAdmin.styles';

const SecurityReport = () => {
  const [reports, setReports] = useState<ReportData>();
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [loader, setLoader] = useState(true);
  //   const [monthRange, setMonthRange] = useState<[null | Date, null | Date]>([
  //     null,
  //     null,
  //   ]);
  //   const [search, setSearch] = useState<string | undefined>('');

  const { currentCompany } = useBaoSelector((state) => state.common);
  const hsId = currentCompany?.company_hs_id
    ? String(currentCompany?.company_hs_id)
    : null;

  const milisecondsToHours = (miliseconds: number) =>
    miliseconds / 1000 / 60 / 60;

  useEffect(() => {
    (async () => {
      if (!hsId) return;

      try {
        const params: IReportParams = {
          company_hs_id: hsId,
          limit: rowsPerPage,
          page: currentPage + 1,
        };

        // if (monthRange[0] && monthRange[1]) {
        //   params.date_from = dayjs(monthRange[0]).format('YYYY-MM-DD');
        //   params.date_to = dayjs(monthRange[1]).format('YYYY-MM-DD');
        // }

        // if (search) {
        //   params.search = search;
        // }

        const { data } = await axios.get<ReportData>(
          `${process.env.REACT_APP_USER_SERVICE}/projects/reports`,
          { params }
        );
        data.projects_reports = data.projects_reports.map((item) => {
          const monthly_budget_in_hours = item.project.monthly_budget_in_hours
            ? Math.round(item.project.monthly_budget_in_hours * 10) / 10
            : 0;

          let credit_balance = monthly_budget_in_hours
            ? monthly_budget_in_hours - milisecondsToHours(item.time_tracked)
            : 0;
          credit_balance = Math.round(credit_balance * 10) / 10;
          const minimalMonthlyBudget = item.project.minimum_monthly_budget
            ? item.project.minimum_monthly_budget
            : 0;
          const overBudgetHourlyRate = item.project.monthly_budget_in_hours
            ? item.project.monthly_budget_in_hours
            : 0;

          let curent_consumption = item.time_tracked ? item.time_tracked : 0;
          curent_consumption = milisecondsToHours(curent_consumption);

          const overConsumedHrs = curent_consumption - monthly_budget_in_hours;
          // number formats
          item.project.monthly_budget_in_hours = monthly_budget_in_hours;
          item.project.curent_consumption =
            Math.round(curent_consumption * 10) / 10;
          item.project.over_consumed_hrs =
            overConsumedHrs > 0 ? Math.round(overConsumedHrs * 10) / 10 : 0;

          item.project.credit_balance = credit_balance;
          let overBudget = 0;
          if (credit_balance < 0) {
            overBudget = Math.abs(credit_balance) * overBudgetHourlyRate;
          }
          item.project.overBudget = overBudget;
          item.project.total_fee = overBudget + minimalMonthlyBudget;
          return item;
        });
        setReports(data);
        setLoader(false);
      } catch (getReportError) {
        // console.log('Failed to get report!', getReportError);
      }
    })();
  }, [hsId, currentPage, rowsPerPage]); // , monthRange, search

  const handleChangePage = (event: unknown, newPage: number) => {
    setLoader(true);
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (option: number) => {
    setLoader(true);
    setCurrentPage(0);
    setRowsPerPage(option);
  };

  const columns = useMemo(
    () => [
      {
        key: 'month',
        label: 'Month',
        sortable: false,
        width: '20%',
        render: ({ month, year }: Report) => (
          <StyledCellText $textType="bold">
            {months[month - 1]}, {year}
          </StyledCellText>
        ),
      },
      {
        key: 'project',
        label: 'Project Name',
        sortable: false,
        width: '40%',
        render: ({ project }: Report) => (
          <StyledCellText $textType="default">{project.name}</StyledCellText>
        ),
      },
      // {
      //   key: 'contract_type',
      //   label: 'Contract type',
      //   sortable: false,
      //   width: '20%',
      //   render: ({ project }: Report) => {
      //     const { contract_type } = project || {};

      //     return (
      //       <StyledCellText
      //         $textType="default"
      //         $color={contract_type ? neutrals[700] : palette.warning[600]}
      //       >
      //         {contract_type
      //           ? contractTypeMap[contract_type] || contract_type
      //           : 'Not set'}
      //       </StyledCellText>
      //     );
      //   },
      // },
      {
        key: 'invoice_number',
        label: 'Invoice details',
        sortable: false,
        width: '40%',
        render: (report: Report) => (
          <StyledCellText
            $color={getCustomerInvoiceColor(report)}
            sx={{
              display: 'inline',
              // Only apply if invoice_qb_id is exists
              ...(report?.invoice_qb_id
                ? {
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }
                : {}),
            }}
          >
            {getCustomerInvoiceInfo(report)}
          </StyledCellText>
        ),
      },
    ],
    []
  );

  return (
    <HeadlessPageWrapper>
      <StyledPagePaper>
        {/* <StyledFilterWrapper>
          <Stack direction="row" gap={3}>
            <ButtonMonthRangePicker
              tooltip="Filter by month"
              onChange={handleMonthRange}
            />

            <FilterAutocomplete
              isFreeSolo={!!search}
              value={search}
              options={[]}
              placeholder="Search by: Project name"
              tooltip="Filter by project"
              onChange={debouncedChangeHandler}
              onReset={onReset}
            />
          </Stack>

          <FilterDetails
            show={!loader && shouldShowReportsDetails}
            data={reportDetails}
          />
        </StyledFilterWrapper> */}
        <StyledTableContainer style={{ minWidth: 800 }}>
          <Table
            idKey="id"
            loading={loader}
            columns={columns}
            data={reports?.projects_reports || []}
          />
          <StyledTablePagination
            labelDisplayedRows={() => null}
            rowsPerPageOptions={[]}
            count={reports?.count || 0}
            rowsPerPage={rowsPerPage}
            page={currentPage}
            onPageChange={handleChangePage}
            ActionsComponent={(props) => (
              <PaginationActions
                {...props}
                labelRowsPerPage="Items per page"
                rowsPerPageOptions={[20, 50, 100, 200]}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          />
        </StyledTableContainer>
      </StyledPagePaper>
    </HeadlessPageWrapper>
  );
};

export default SecurityReport;
